import React, { useEffect, useState } from "react";
import { Search, FileText, Mail, HelpCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Button } from "../components/ui";
import { Link } from "react-router-dom";
import { PERFORMANCE_FEE_DISCLAIMER } from "../helpers/constants";
import { trackPageViewOnce, trackPortalCta } from "../lib/siteActivity";

const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    trackPageViewOnce("/help", "Help Center");
  }, []);

  const categories = [
    { id: "filing", name: "Filing Process", icon: FileText },
    { id: "billing", name: "Billing & Fees", icon: Mail },
  ];

  const faqs = [
    {
      category: "filing",
      question: "What is a phantom asset?",
      answer:
        "A phantom asset is machinery or equipment that has been scrapped, sold, relocated, or fully depreciated but remains on your county Business Personal Property (BPP) tax roll, so you continue paying tax on assets no longer in service.",
      popular: true,
    },
    {
      category: "filing",
      question: "How do I start a free Business Personal Property tax audit?",
      answer:
        "Create a secure corporate portal account, then upload your County BPP Assessment Notice (PDF) and your corporate equipment asset ledger (PDF or CSV) into your encrypted ledger room to generate your initial 60-second diagnostic.",
      popular: true,
    },
    {
      category: "filing",
      question: "What documents do I need to provide?",
      answer:
        "Your latest County Business Personal Property Assessment Notice and an itemized fixed-asset ledger or depreciation schedule covering machinery, tools, furniture, and similar personal property, not buildings or land.",
      popular: true,
    },
    {
      category: "billing",
      question: "How does the 35% performance-fee structure work?",
      answer:
        "SolvTax is risk-free. We charge a 35% performance fee exclusively on realized, verified reductions to your BPP tax bill. If the audit finds no actionable over-assessment variances, you pay $0.",
      popular: true,
    },
    {
      category: "filing",
      question: "How do you analyze my tax notice?",
      answer:
        "We parse your county BPP notice and cross-reference each equipment line against your ledger and statutory depreciation schedules, including assessment ratios such as Missouri’s 33.33% of historical cost to flag phantom assets and misclassified useful lives.",
      popular: true,
    },
    {
      category: "filing",
      question: "How is my business property data secured?",
      answer:
        "All corporate tax filings and asset depreciation ledgers are managed within your encrypted, password-protected SolvTax Portal. We require corporate identity validation to protect sensitive financial records.",
      popular: false,
    },
    {
      category: "filing",
      question: "What file formats are supported for document upload?",
      answer:
        "Your County BPP Assessment Notice must be a PDF. Your internal asset ledger or depreciation schedule can be PDF or CSV from your accounting software.",
      popular: false,
    },
    {
      category: "filing",
      question: "How fast is the initial audit?",
      answer:
        "Our engine cross-references your asset descriptions against state-mandated depreciation schedules and percent-good tables the moment both documents are uploaded. Most diagnostics complete in under 60 seconds.",
      popular: false,
    },
    {
      category: "billing",
      question: "Are there any upfront costs or retainers?",
      answer:
        "No. SolvTax charges $0 upfront. You only pay a 35% performance fee on verified savings we successfully recover. If we find no discrepancies, your fee is $0.",
      popular: false,
    },
  ];

  const filteredFaqs = faqs.filter((faq) => {
    const matchesCategory = selectedCategory === "all" || faq.category === selectedCategory;
    const matchesSearch =
      searchQuery === "" ||
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const popularFaqs = faqs.filter((faq) => faq.popular);

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-neutral-900 mb-4">How can we help you?</h1>
            <p className="text-xl text-neutral-600 mb-8 max-w-2xl mx-auto">
              Answers about Business Personal Property (BPP) tax audits, phantom assets, equipment
              document intake, and our 35% performance-fee model
            </p>

            {/* Search */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-3.5 h-5 w-5 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Search for help..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 text-lg border border-neutral-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>Browse by Category</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                        selectedCategory === category.id
                          ? "bg-primary text-white"
                          : "hover:bg-neutral-100 text-neutral-700"
                      }`}
                    >
                      <category.icon className="h-4 w-4" />
                      {category.name}
                    </button>
                  ))}
                  {selectedCategory !== "all" && (
                    <button
                      onClick={() => setSelectedCategory("all")}
                      className="w-full p-3 rounded-lg text-left text-sm text-neutral-500 hover:bg-neutral-100 transition-colors"
                    >
                      Show all topics
                    </button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Popular FAQs */}
            {searchQuery === "" && selectedCategory === "all" && (
              <div className="mb-8 max-w-4xl mx-auto w-full px-6">
                <h2 className="text-2xl font-bold text-neutral-900 mb-6 text-left">
                  Popular Questions
                </h2>
                <div className="space-y-4">
                  {popularFaqs.map((faq, index) => (
                    <div
                      key={index}
                      className="bg-white border border-slate-200 shadow-sm rounded-xl p-6 text-slate-950"
                    >
                      <h3 className="font-semibold text-slate-950 mb-2 text-left">
                        {faq.question}
                      </h3>
                      <p className="text-slate-700 text-left leading-relaxed">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* All FAQs */}
            <div className="max-w-4xl mx-auto w-full px-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-neutral-900 text-left">
                  {searchQuery || selectedCategory !== "all" ? "Search Results" : "All Questions"}
                </h2>
                <span className="text-sm text-neutral-500">
                  {filteredFaqs.length} result
                  {filteredFaqs.length !== 1 ? "s" : ""}
                </span>
              </div>

              {filteredFaqs.length === 0 ? (
                <Card>
                  <CardContent className="text-center p-12">
                    <HelpCircle className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-neutral-900 mb-2">No results found</h3>
                    <p className="text-neutral-600 mb-6">
                      Try adjusting your search terms or browse by category
                    </p>
                    <Button variant="outline" onClick={() => setSearchQuery("")}>
                      Clear Search
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {filteredFaqs.map((faq, index) => (
                    <div
                      key={index}
                      className="bg-white border border-slate-200 shadow-sm rounded-xl p-6 text-slate-950"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <HelpCircle className="h-4 w-4 text-slate-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-950 mb-2 text-left">
                            {faq.question}
                          </h3>
                          <p className="text-slate-700 text-left leading-relaxed">{faq.answer}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Still need help? */}
            <Card variant="gradient" className="mt-8 max-w-4xl mx-auto w-full">
              <CardContent className="p-6">
                <div className="text-center">
                  <Mail className="h-8 w-8 text-primary mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">Still need help?</h3>
                  <p className="text-neutral-600 mb-6">
                    Email our team. We regularly respond within one business day.
                  </p>
                  <p className="text-neutral-600 mb-2 text-sm whitespace-pre-line">
                    {PERFORMANCE_FEE_DISCLAIMER}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link to="/contact">
                      <Button variant="primary" className="gap-2">
                        <Mail className="h-4 w-4" />
                        Email Support
                      </Button>
                    </Link>
                    <Link to="/?triggerSignup=true" onClick={() => trackPortalCta("help-cta")}>
                      <Button variant="outline" className="gap-2">
                        Request Access
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
